/**
 * validate_flow tool - Validate a Power Automate flow definition
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import type { FlowDefinition, ConnectionReference } from "../api/types.js";
import {
  validateFlow,
  getAllValidationErrors,
  getAllValidationWarnings,
  checkBestPractices,
} from "../validator/index.js";
import { logger } from "../utils/logger.js";

const triggerSchema = z.object({
  type: z.string(),
  kind: z.string().optional(),
  inputs: z.record(z.string(), z.unknown()).optional(),
  recurrence: z.object({
    frequency: z.enum(["Second", "Minute", "Hour", "Day", "Week", "Month"]),
    interval: z.number().min(1),
    startTime: z.string().optional(),
    timeZone: z.string().optional(),
  }).optional(),
  conditions: z.array(z.object({ expression: z.string() })).optional(),
  splitOn: z.string().optional(),
});

interface ActionSchemaType {
  type: string;
  inputs?: unknown;
  runAfter?: Record<string, string[]>;
  actions?: Record<string, ActionSchemaType>;
  else?: { actions: Record<string, ActionSchemaType> };
  expression?: unknown;
  foreach?: string;
}

const actionSchema: z.ZodType<ActionSchemaType> = z.object({
  type: z.string(),
  // Inputs are any JSON: Compose takes a string or an expression, Terminate takes
  // an object, Set variable takes a value. Demanding an object rejected valid flows.
  inputs: z.unknown().optional(),
  runAfter: z.record(z.string(), z.array(z.string())).optional(),
  actions: z.record(z.string(), z.lazy(() => actionSchema)).optional(),
  else: z.object({ actions: z.record(z.string(), z.lazy(() => actionSchema)) }).optional(),
  expression: z.unknown().optional(),
  foreach: z.string().optional(),
});

const connectionRefSchema = z.object({
  connectionName: z.string(),
  id: z.string(),
  source: z.enum(["Invoker", "Embedded"]).optional(),
  tier: z.enum(["Standard", "Premium", "NotSpecified"]).optional(),
});

export const validateFlowInputSchema = z.object({
  flowId: z
    .string()
    .optional()
    .describe("Existing flow ID to validate. If provided, fetches and validates the flow from the API."),
  definition: z
    .object({
      $schema: z.string().optional(),
      contentVersion: z.string().optional(),
      triggers: z.record(z.string(), triggerSchema),
      actions: z.record(z.string(), actionSchema),
      parameters: z.record(z.string(), z.unknown()).optional(),
    })
    .optional()
    .describe("Flow definition to validate. Use this to validate a new flow before creating it."),
  connectionReferences: z
    .record(z.string(), connectionRefSchema)
    .optional()
    .describe("Connection references to validate against the definition."),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID when fetching an existing flow."),
});

export type ValidateFlowInput = z.infer<typeof validateFlowInputSchema>;

export const validateFlowTool = {
  name: "validate_flow",
  description:
    "Validate a Power Automate flow definition for errors. Can validate either an existing flow by ID or a new definition. Checks for schema errors, expression syntax, circular dependencies, and missing connection references.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "Existing flow ID to validate. Fetches and validates the flow.",
      },
      definition: {
        type: "object",
        description: "Flow definition to validate (for new flows).",
        properties: {
          "$schema": { type: "string" },
          contentVersion: { type: "string" },
          triggers: { type: "object" },
          actions: { type: "object" },
          parameters: { type: "object" },
        },
        required: ["triggers", "actions"],
      },
      connectionReferences: {
        type: "object",
        description: "Connection references to validate.",
      },
      environment: {
        type: "string",
        description: "Environment when fetching an existing flow.",
      },
    },
  },
};

/**
 * `runAfter` must name a sibling action. A dangling reference is accepted by
 * the save API and then fails at run time as ActionConditionFailed on every
 * run, so catch it here. Nested scopes (`actions`, `else.actions`, and the
 * branches of a Switch) are their own sibling sets.
 */
export function findDanglingRunAfter(actions: object | undefined, path = ""): string[] {
  if (!actions || typeof actions !== "object") return [];
  const map = actions as Record<string, unknown>;
  const siblings = new Set(Object.keys(map));
  const problems: string[] = [];
  for (const [name, raw] of Object.entries(map)) {
    if (!raw || typeof raw !== "object") continue;
    const action = raw as Record<string, unknown>;
    const runAfter = action["runAfter"];
    if (runAfter && typeof runAfter === "object") {
      for (const dep of Object.keys(runAfter)) {
        if (!siblings.has(dep)) {
          problems.push(`Action '${path}${name}' runs after '${dep}', which does not exist at that level — it would be skipped on every run (ActionConditionFailed).`);
        }
      }
    }
    const nested = action["actions"];
    if (nested && typeof nested === "object") problems.push(...findDanglingRunAfter(nested, `${path}${name}/`));
    const elseBranch = action["else"];
    if (elseBranch && typeof elseBranch === "object") {
      const elseActions = (elseBranch as Record<string, unknown>)["actions"];
      if (elseActions && typeof elseActions === "object") problems.push(...findDanglingRunAfter(elseActions, `${path}${name}/else/`));
    }
    const cases = action["cases"];
    if (cases && typeof cases === "object") {
      for (const [caseName, c] of Object.entries(cases as Record<string, unknown>)) {
        const caseActions = c && typeof c === "object" ? (c as Record<string, unknown>)["actions"] : undefined;
        if (caseActions && typeof caseActions === "object") problems.push(...findDanglingRunAfter(caseActions, `${path}${name}/${caseName}/`));
      }
    }
    const def = action["default"];
    if (def && typeof def === "object") {
      const defActions = (def as Record<string, unknown>)["actions"];
      if (defActions && typeof defActions === "object") problems.push(...findDanglingRunAfter(defActions, `${path}${name}/default/`));
    }
  }
  return problems;
}

export async function handleValidateFlow(
  api: FlowManagementApi,
  input: unknown
): Promise<string> {
  const parsed = validateFlowInputSchema.parse(input);

  logger.info(
    { flowId: parsed.flowId, hasDefinition: !!parsed.definition },
    "Executing validate_flow"
  );

  let definition: FlowDefinition;
  let connectionReferences: Record<string, ConnectionReference> = {};
  if (parsed.flowId) {
    // Validate existing flow from API
    const flow = await api.getFlow(parsed.flowId, parsed.environment);
    definition = flow.properties.definition;
    connectionReferences = flow.properties.connectionReferences;
  } else if (parsed.definition) {
    // Validate provided definition
    definition = {
      $schema:
        parsed.definition.$schema ??
        "https://schema.management.azure.com/providers/Microsoft.Logic/schemas/2016-06-01/workflowdefinition.json#",
      contentVersion: parsed.definition.contentVersion ?? "1.0.0.0",
      triggers: parsed.definition.triggers,
      actions: parsed.definition.actions as FlowDefinition["actions"],
      parameters: parsed.definition.parameters as FlowDefinition["parameters"],
    };

    if (parsed.connectionReferences) {
      for (const [key, ref] of Object.entries(parsed.connectionReferences)) {
        connectionReferences[key] = {
          connectionName: ref.connectionName,
          id: ref.id,
          source: ref.source ?? "Embedded",
          tier: ref.tier ?? "NotSpecified",
        };
      }
    }
  } else {
    return "Please provide either a flowId to validate an existing flow, or a definition for a new flow.";
  }

  // Perform validation
  const result = validateFlow(definition, connectionReferences);
  const errors = [...findDanglingRunAfter(definition.actions), ...getAllValidationErrors(result)];
  const warnings = getAllValidationWarnings(result);
  const valid = result.valid && errors.length === 0;

  // Perform best practice checks
  const bestPractices = checkBestPractices(definition, connectionReferences);

  const lines: string[] = [];

  if (valid) {
    lines.push(`✅ **Validation Passed**`);
    lines.push(``);
    lines.push(`**Summary:**`);
    lines.push(`• Triggers: ${Object.keys(definition.triggers).length}`);
    lines.push(`• Actions: ${Object.keys(definition.actions).length}`);
    lines.push(`• Connections: ${Object.keys(connectionReferences).length}`);
    lines.push(`• Expressions: ${result.expressionValidation.expressionCount}`);
    lines.push(``);

    if (warnings.length > 0) {
      lines.push(`**Warnings (${warnings.length}):**`);
      warnings.slice(0, 5).forEach(w => lines.push(`  ⚠️ ${w}`));
      if (warnings.length > 5) lines.push(`  ... and ${warnings.length - 5} more`);
      lines.push(``);
    }
  } else {
    lines.push(`❌ **Validation Failed** (${errors.length} error${errors.length > 1 ? "s" : ""})`);
    lines.push(``);

    errors.slice(0, 10).forEach(e => {
      lines.push(`• ${e}`);
    });

    if (errors.length > 10) {
      lines.push(``);
      lines.push(`... and ${errors.length - 10} more errors`);
    }

    if (warnings.length > 0) {
      lines.push(``);
      lines.push(`Also ${warnings.length} warning${warnings.length > 1 ? "s" : ""}.`);
    }
    lines.push(``);
  }

  // Add best practice results
  lines.push(`---`);
  lines.push(`**Best Practices Score: ${bestPractices.score}/100**`);
  lines.push(``);

  if (bestPractices.passed.length > 0) {
    lines.push(`✅ ${bestPractices.passed.join(", ")}`);
  }

  const bpWarnings = bestPractices.issues.filter(i => i.severity === "warning");
  const bpSuggestions = bestPractices.issues.filter(i => i.severity === "suggestion");

  if (bpWarnings.length > 0) {
    lines.push(``);
    lines.push(`**Warnings:**`);
    for (const issue of bpWarnings.slice(0, 3)) {
      lines.push(`  ⚠️ ${issue.message}`);
      lines.push(`     Fix: ${issue.fix}`);
    }
  }

  if (bpSuggestions.length > 0) {
    lines.push(``);
    lines.push(`**Suggestions:**`);
    for (const issue of bpSuggestions.slice(0, 3)) {
      lines.push(`  💡 ${issue.message}`);
      lines.push(`     Fix: ${issue.fix}`);
    }
  }

  if (bestPractices.issues.length === 0) {
    lines.push(`🎉 Flow follows all best practices!`);
  }

  return lines.join("\n");
}
